import { Graphics } from "pixi.js"

// The HUD's "look here" flare: the shape shared by every widget that arrives UNASKED, because somebody
// else did something -- another player logging in (the players chip), or inviting you to their camp
// (the invitation prompt). Such a widget appears in the corner of a screen whose middle is usually a
// wave in progress, so it has to announce itself or it is simply never seen.
//
// One flare, one colour, one length, so "look here" means the same thing wherever it happens. What a
// widget varies is only how far the glow reaches and how hard it pops -- a 190px panel does not need
// to swell the way a 38px chip does.
//
// Everything here is pure, so the shape of the thing can be asserted without a renderer.

// The hot orange the highlighted roster edge-arrow uses.
export const FLARE_COLOR = 0xff8a1f
export const FLARE_MS = 3000

// Peaks land roughly every two thirds of a second. Tied to FLARE_MS: stretching the flare without
// adding pulses would leave it swelling and sagging too slowly to read as a signal.
const FLARE_PULSES = 4.5

// The fraction of the flare spent fading out. Before it, the pulses run at full strength; a flare
// that started decaying immediately would be three seconds long but only read as one, because a
// squared decay has already thrown most of the brightness away by the halfway mark.
const FADE_FROM = 0.45

// How much bigger a small widget is drawn at the peak of a pulse. Callers with more area to lose
// (a docked panel, whose neighbours are right against it) pass something gentler.
export const SCALE_POP = 0.2

// The glow bleeding off the plate: GLOW_LAYERS strokes stepping out to the caller's spread, each
// dimmer than the one inside it.
export const GLOW_SPREAD = 7
const GLOW_LAYERS = 3
const GLOW_ALPHA = 0.42

// The rings that travel out of the plate, further than the glow and thinning as they go. Two of them,
// half a cycle apart, so one is always leaving as the next arrives.
export const RING_SPREAD = 13
export const RING_COUNT = 2

// How much of the flare's strength is left with `remaining` (1..0) of it to go. Full until the fade
// begins, then squared down to exactly zero, which is what guarantees the thing ends.
const envelopeAt = (remaining: number): number =>
    remaining >= FADE_FROM ? 1 : (remaining / FADE_FROM) * (remaining / FADE_FROM)

// How hot the flare is `t` ms into it: 1 at its peak, 0 once it is over. A pulse train under that
// envelope -- the pulses keep the widget moving (a single fade reads as a rendering hiccup) and the
// envelope is what lands it.
export const flareIntensity = (remainingMs: number): number => {
    if (!(remainingMs > 0)) {
        return 0
    }
    const remaining = Math.min(1, remainingMs / FLARE_MS)
    // Pulses run on the ELAPSED fraction so the train starts hot at its first peak rather than at
    // zero.
    const elapsed = 1 - remaining
    const pulse = Math.abs(Math.cos(elapsed * Math.PI * FLARE_PULSES))
    return pulse * envelopeAt(remaining)
}

// How much bigger a widget is drawn `t` ms into the flare. Exactly 1 once it is over, so a settled
// widget is the size its row was laid out for.
export const flareScale = (remainingMs: number, pop: number = SCALE_POP): number =>
    1 + flareIntensity(remainingMs) * pop

// The expanding rings, as a fraction of the spread travelled and how solid each one still is. They
// ride the same envelope as the plate, so the whole flare lands together rather than leaving a ring
// in the air after the colour has gone.
export const flareRings = (remainingMs: number): { progress: number; alpha: number }[] => {
    if (!(remainingMs > 0)) {
        return []
    }
    const remaining = Math.min(1, remainingMs / FLARE_MS)
    const elapsed = 1 - remaining
    const envelope = envelopeAt(remaining)
    const rings: { progress: number; alpha: number }[] = []
    for (let i = 0; i < RING_COUNT; i++) {
        const progress = (elapsed * FLARE_PULSES + i / RING_COUNT) % 1
        rings.push({ progress, alpha: (1 - progress) * envelope })
    }
    // A ring too faint to see is a stroke the GPU draws for nothing.
    return rings.filter((ring) => ring.alpha > 0.02)
}

// Blend `from` toward `to` by `amount` (0..1), per channel. Used rather than pixi's tint because a
// plate is a filled Graphics whose fill AND rim have to move together.
export const blendColor = (from: number, to: number, amount: number): number => {
    const a = Math.max(0, Math.min(1, amount))
    const mix = (shift: number) => {
        const f = (from >> shift) & 0xff
        const t = (to >> shift) & 0xff
        return Math.round(f + (t - f) * a) & 0xff
    }
    return (mix(16) << 16) | (mix(8) << 8) | mix(0)
}

// Paint the halo around a plate of `width` x `height`: nested strokes and travelling rings rather
// than a blur filter, because a filter on a HUD widget costs a render target and a pass every frame,
// and the HUD is where budget hardware notices. Draws nothing once the flare is over, so a settled
// widget costs one cleared Graphics.
export const drawFlareGlow = (
    glow: Graphics,
    {
        remainingMs,
        width,
        height,
        corner,
        spread = GLOW_SPREAD,
        ringSpread = RING_SPREAD,
    }: { remainingMs: number; width: number; height: number; corner: number; spread?: number; ringSpread?: number },
) => {
    glow.clear()
    if (remainingMs <= 0) {
        return
    }
    const hot = flareIntensity(remainingMs)
    // At the trough between two pulses there is no light to draw, only rings still travelling.
    for (let i = 1; hot > 0.01 && i <= GLOW_LAYERS; i++) {
        const out = ((spread * i) / GLOW_LAYERS) * hot
        // Inner layers are the brightest, so the light looks like it is coming off the plate.
        glow.lineStyle(2.5, FLARE_COLOR, hot * GLOW_ALPHA * (1 - (i - 1) / GLOW_LAYERS))
        glow.drawRoundedRect(-out, -out, width + out * 2, height + out * 2, corner + out)
    }
    for (const ring of flareRings(remainingMs)) {
        const out = ringSpread * ring.progress
        glow.lineStyle(0.5 + 2 * (1 - ring.progress), FLARE_COLOR, ring.alpha * 0.75)
        glow.drawRoundedRect(-out, -out, width + out * 2, height + out * 2, corner + out)
    }
}
